import type { GrowthBook } from '@growthbook/growthbook'

/**
 * Composable for managing feature flags using GrowthBook. Refer to https://zunocarbon.atlassian.net/wiki/spaces/DEV/pages/767721475/Guide+Growthbook+Feature+Flag
 */
export function useFeatureFlag() {
  const nuxtApp = useNuxtApp()
  const growthbook = nuxtApp.$growthbook as GrowthBook

  if (!growthbook) {
    throw new Error('GrowthBook is not initialized')
  }

  /**
   * Sets user attributes for feature flag targeting
   */
  async function setAttributes(attributes: Record<string, string>) {
    await growthbook.setAttributes(attributes)
  }

  /**
   * Retrieves current user attributes
   */
  const getAttributes = () => growthbook.getAttributes()

  /**
   * Checks if the translation widget feature is enabled
   */
  const isTranslationWidgetEnabled = () => growthbook.isOn('ff-enable-translation-widget')

  /**
   * Checks if the ESG dashboard widget feature is enabled.
   * @beta
   */
  const isEsgDashboardEnabled = () => growthbook.isOn('ff-enable-esg-dashboard-widget')

  /**
   * Gets the current maintenance message if any
   * If it is not null, the maintenance banner will be displayed on the dashboard
   */
  const maintenanceMessage = () => growthbook.getFeatureValue('maintenance-message', null)

  /**
   * Checks if the supplier portal feature is enabled
   * @beta
   */
  const isSupplierPortalEnabled = () => growthbook.isOn('ff-enable-supplier-portal')

  /**
   * Checks if the new revamped navbar feature is enabled
   * @beta
   */
  const isRevampedNavbarEnabled = () => growthbook.isOn('ff-enable-revamped-navbar')

  /**
   * Checks if ESG Revamped Menus are enabled
   * @beta
   */
  const isEsgRevampedEnabled = () => growthbook.isOn('ff-enable-esg-revamp')

  /**
   * Checks if ESG Submission Revamp is enabled
   * @beta
   */
  const isEsgSubmissionRevampEnabled = () => growthbook.isOn('ff-enable-esg-submission-revamp')

  /**
   * Checks if ESG Data Management Revamp is enabled
   * @beta
   */
  const isGhgAssignmentRevampEnabled = () => growthbook.isOn('ff-enable-ghg-assignment-revamp')

  /**
 * Checks if the Custom dashboard feature is enabled.
 * @beta
 */
  const isCustomDashboardEnabled = () => growthbook.isOn('ff-enable-custom-dashboard')

  /**
   * Gets the relevant PBI dashboard URI
   * If it is not null, the client's custom dashboard will be displayed
   */
  const customDashboardURI = () => growthbook.getFeatureValue('custom-dashboard-uri', null)

  /**
   * Checks if the new User Assignment is enabled
   * @beta
   */
  const isNewUserAssignmentEnabled = () => growthbook.isOn('ff-enable-new-user-assignment')

  /**
   * Checks if the view by user is enabled
   * @beta
   */
  const isViewByUserEnabled = () => growthbook.isOn('ff-enable-view-by-user')

  /**
   * Checks if the new dashboard is enabled
   * @beta
   */
  const isOptimizedDashboardEnabled = () => growthbook.isOn('ff-enable-optimized-dashboard')

  /**
   * Checks if the manage users feature is enabled
   * @beta
   */
  const isManageUsersEnabled = () => growthbook.isOn('ff-enable-manage-users')

  /**
   * Checks if editing approved submissions is disabled
   * @beta
   */
  const isEditAfterApprovedDisabled = () => growthbook.isOn('ff-disable-edit-after-approved')

  /**
   * Checks if ESG anomaly detection is enabled
   * @beta
   */
  const isEsgAnomalyDetectionEnabled = () => growthbook.isOn('ff-enable-esg-anomaly-detection')
  /**
   * Checks if the client template feature is enabled
   * @beta
   */
  const isClientTemplateEnabled = () => growthbook.isOn('ff-enable-client-template')

  /**
   * Checks if the tenant's subscription has expired
   */
  const isTenantSubscriptionExpired = () => growthbook.isOn('ff-tenant-subscription-expired')

  /**
   * Checks if the reporting side app (external) is enabled.
   * When enabled, the Report link navigates to /reporting/ externally.
   * When disabled, it navigates internally to /dashboard/reporting/report-frameworks.
   * @beta
   */
  const isReportingSideAppEnabled = () => growthbook.isOn('ff-enable-reporting-side-app')

  /**
   *  Checks if the user management feature is enabled.
   *  this role is specifically for RWS.
   */
  const isUserManagerEnabled = () => growthbook.isOn('ff-enable-user-management')

  return {
    getAttributes,
    setAttributes,
    isTranslationWidgetEnabled,
    isEsgDashboardEnabled,
    maintenanceMessage,
    isSupplierPortalEnabled,
    isRevampedNavbarEnabled,
    isEsgRevampedEnabled,
    isEsgSubmissionRevampEnabled,
    isGhgAssignmentRevampEnabled,
    isCustomDashboardEnabled,
    customDashboardURI,
    isNewUserAssignmentEnabled,
    isViewByUserEnabled,
    isOptimizedDashboardEnabled,
    isManageUsersEnabled,
    isEditAfterApprovedDisabled,
    isEsgAnomalyDetectionEnabled,
    isClientTemplateEnabled,
    isTenantSubscriptionExpired,
    isReportingSideAppEnabled,
    isUserManagerEnabled,
  }
}
